import type { ChargePaymentMethod } from '../api/schemas';

type PaymentMethodOption = { value: ChargePaymentMethod; label: string };

/** The ways money changes hands, in the order they are offered. */
export const PAYMENT_METHODS: PaymentMethodOption[] = [
  { value: 'efectivo', label: 'Efectivo' },
  { value: 'tarjeta', label: 'Tarjeta' },
  { value: 'yappy', label: 'Yappy' },
  { value: 'transferencia', label: 'Transferencia' },
  { value: 'banco_aliado', label: 'Banco Aliado' },
  { value: 'otro', label: 'Otro' },
];

/**
 * Methods the counter confirms by hand from a receipt it was shown (until Dale
 * integrates Banco Aliado's API). The server refuses them without a reference.
 */
const REFERENCE_REQUIRED: readonly ChargePaymentMethod[] = ['transferencia', 'banco_aliado'];

/** Mirrors the API's `charges.payment_reference` rules (migration 0080). */
export const PAYMENT_REFERENCE_MAX = 60;
const PAYMENT_REFERENCE_PATTERN = /^[\p{L}\p{N} /#-]*$/u;

export function requiresPaymentReference(method: ChargePaymentMethod): boolean {
  return REFERENCE_REQUIRED.includes(method);
}

/**
 * For the flows that carry no reference field (a comanda's settle, selling a
 * paquete): their API routes take only these methods.
 */
export const PAYMENT_METHODS_WITHOUT_REFERENCE: PaymentMethodOption[] = PAYMENT_METHODS.filter(
  (entry) => !requiresPaymentReference(entry.value),
);

export function paymentMethodLabel(method: ChargePaymentMethod): string {
  return PAYMENT_METHODS.find((entry) => entry.value === method)?.label ?? method;
}

/** The same words the API answers with, so the counter hears one voice. */
export function paymentReferenceError(method: ChargePaymentMethod, value: string): string | undefined {
  const reference = value.trim();
  if (reference === '') {
    return requiresPaymentReference(method) ? 'Escribe la referencia del pago.' : undefined;
  }
  if (reference.length > PAYMENT_REFERENCE_MAX) {
    return `La referencia admite hasta ${PAYMENT_REFERENCE_MAX} caracteres.`;
  }
  if (!PAYMENT_REFERENCE_PATTERN.test(reference)) {
    return 'La referencia solo admite letras, números, espacios y los signos - / #.';
  }
  return undefined;
}

/** "Banco Aliado · Ref. BA-123", or just the method when no reference was kept. */
export function paymentMethodWithReference(method: ChargePaymentMethod, reference?: string | null): string {
  const label = paymentMethodLabel(method);
  return reference ? `${label} · Ref. ${reference}` : label;
}
